/** Sub-components for EntityDetail. */
const CLASSIFICATIONS = ["UNCLASSIFIED", "CONFIDENTIAL", "SECRET", "OMEGA"];
const STATUSES = ["ACTIVE", "REDACTED", "LOST"];
const THREATS = ["—", "I", "II", "III", "IV", "V", "VI", "VII"];

export function statusClass(status) {
  if (status === "ACTIVE") return "status-active";
  if (status === "REDACTED") return "status-redacted";
  return "status-lost";
}

export function EmptyBody({ label = "no description recorded" }) {
  return (
    <div className="border border-dashed border-[#333] p-10 text-center text-[#555] text-xs tracking-widest uppercase">
      ::: {label} :::
    </div>
  );
}

export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function EntityHeader({ entity, editing, draft, setDraft, e }) {
  return (
    <header className="grid grid-cols-1 lg:grid-cols-12 gap-8 border-b border-[#333] pb-10">
      <div className="lg:col-span-8 space-y-5">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs tracking-[0.3em] text-[#A3A3A3]">ENTITY</span>
          <span className="text-sm tracking-[0.3em] text-[#E3E1D9]">{entity.id}</span>
          <span className={`status-pill ${statusClass(e.status)}`}>{e.status}</span>
          {e.threat_level && <span className="tiv-stamp">Stratum {e.threat_level}</span>}
        </div>
        {editing ? (
          <>
            <input className="tiv-input text-3xl" value={draft.name}
              onChange={(ev) => setDraft({ ...draft, name: ev.target.value })}
              placeholder="entity name..." data-testid="entity-edit-name" />
            <input className="tiv-input" value={draft.title}
              onChange={(ev) => setDraft({ ...draft, title: ev.target.value })}
              placeholder="title / epithet" data-testid="entity-edit-title" />
          </>
        ) : (
          <>
            <h1 className="font-serif-display text-4xl sm:text-5xl leading-tight tracking-tighter">
              {entity.name || <span className="text-[#555] italic">[ unnamed ]</span>}
            </h1>
            {entity.title && (
              <div className="text-sm tracking-[0.3em] uppercase text-[#A3A3A3]">{entity.title}</div>
            )}
          </>
        )}
      </div>
      <IndexCard entity={entity} editing={editing} draft={draft} setDraft={setDraft} />
    </header>
  );
}

function IndexCard({ entity, editing, draft, setDraft }) {
  return (
    <aside className="lg:col-span-4 border border-[#333] p-5 space-y-3 text-xs font-mono-data bg-[#0a0a0a]">
      <div className="section-label">
        <span>Index Card</span>
        <span>{entity.id.slice(0, 6)}</span>
      </div>
      {editing ? (
        <div className="space-y-3">
          <SelectField label="Type" inputProps={{
            value: draft.entity_type, "data-testid": "entity-edit-type",
            placeholder: "Demon / Aberration / Mortal...",
            onChange: (ev) => setDraft({ ...draft, entity_type: ev.target.value })
          }} />
          <SelectField label="Threat Stratum" select options={THREATS} value={draft.threat_level}
            testid="entity-edit-threat"
            onChange={(ev) => setDraft({ ...draft, threat_level: ev.target.value === "—" ? "" : ev.target.value })} />
          <SelectField label="Classification" select options={CLASSIFICATIONS} value={draft.classification}
            testid="entity-edit-classification"
            onChange={(ev) => setDraft({ ...draft, classification: ev.target.value })} />
          <SelectField label="Status" select options={STATUSES} value={draft.status}
            testid="entity-edit-status"
            onChange={(ev) => setDraft({ ...draft, status: ev.target.value })} />
          <SelectField label="First Encounter" inputProps={{
            value: draft.date, "data-testid": "entity-edit-date", placeholder: "MMXXIV.X.31",
            onChange: (ev) => setDraft({ ...draft, date: ev.target.value })
          }} />
        </div>
      ) : (
        <div className="space-y-2 text-[#A3A3A3]">
          <Row label="Type" value={entity.entity_type} />
          <Row label="Threat" value={entity.threat_level} />
          <Row label="Class" value={entity.classification} />
          <Row label="Status" value={entity.status} />
          <Row label="First seen" value={entity.date} />
        </div>
      )}
    </aside>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between">
      <span>{label}</span>
      <span className="text-[#E3E1D9]">{value || "—"}</span>
    </div>
  );
}

function SelectField({ label, select, options, value, onChange, testid, inputProps }) {
  return (
    <label className="block">
      <div className="text-[0.6rem] tracking-[0.3em] uppercase text-[#555] mb-1">{label}</div>
      {select ? (
        <select className="tiv-input" value={value} onChange={onChange} data-testid={testid}>
          {options.map((c) => <option key={c} value={c} className="bg-[#0a0a0a]">{c}</option>)}
        </select>
      ) : (
        <input className="tiv-input" {...inputProps} />
      )}
    </label>
  );
}

export function EntityBodySection({ editing, draft, entity, setDraft }) {
  return (
    <section>
      <div className="section-label">
        <span>Observation · Description</span>
        <span>§ folio</span>
      </div>
      {editing ? (
        <textarea className="tiv-textarea" value={draft.body}
          onChange={(ev) => setDraft({ ...draft, body: ev.target.value })}
          placeholder="describe the entity..." data-testid="entity-edit-body" />
      ) : entity.body ? (
        <div className="text-base leading-loose whitespace-pre-wrap max-w-3xl">{entity.body}</div>
      ) : <EmptyBody />}
    </section>
  );
}

export function EntitySightingsSection({ editing, draft, entity, setDraft }) {
  return (
    <section>
      <div className="section-label">
        <span>Sightings · Encounters</span>
        <span>log</span>
      </div>
      {editing ? (
        <textarea className="tiv-textarea" value={draft.sightings}
          onChange={(ev) => setDraft({ ...draft, sightings: ev.target.value })}
          placeholder="one per line — date, place, observer..." data-testid="entity-edit-sightings" />
      ) : entity.sightings ? (
        <div className="text-sm leading-loose whitespace-pre-wrap font-mono-data text-[#A3A3A3] max-w-3xl border-l-2 border-[#333] pl-4">
          {entity.sightings}
        </div>
      ) : <EmptyBody label="no sightings on record" />}
    </section>
  );
}

export function PlatesSection({ images, editing, onUploadImage, removeImage, testidPrefix = "entity" }) {
  return (
    <section>
      <div className="section-label">
        <span>Plates · Likeness</span>
        <span>{(images || []).length}</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {(images || []).map((src, i) => (
          <div key={`${src.slice(-24)}-${i}`} className="border border-[#333] aspect-square overflow-hidden relative group" data-testid={`${testidPrefix}-img-${i}`}>
            <img src={src} alt={`plate-${i}`} className="w-full h-full object-cover tiv-img" />
            {editing && (
              <button
                onClick={() => removeImage(i)}
                className="absolute top-1 right-1 tiv-btn tiv-btn-danger text-[0.55rem] px-2 py-1 opacity-0 group-hover:opacity-100"
                data-testid={`${testidPrefix}-img-remove-${i}`}
              >Burn</button>
            )}
          </div>
        ))}
        {editing && (
          <label className="border border-dashed border-[#333] aspect-square flex items-center justify-center cursor-crosshair hover:border-[#E3E1D9]" data-testid={`${testidPrefix}-img-upload`}>
            <input type="file" accept="image/*" className="hidden" onChange={onUploadImage} />
            <span className="text-xs tracking-[0.3em] uppercase text-[#A3A3A3]">+ Affix Plate</span>
          </label>
        )}
      </div>
    </section>
  );
}

export function EntityActionBar({ editing, saving, onSave, onCancel, onEdit, onRemove }) {
  return (
    <div className="flex flex-wrap gap-3 border-t border-[#333] pt-8">
      {editing ? (
        <>
          <button className="tiv-btn" onClick={onSave} disabled={saving} data-testid="entity-save-btn">
            {saving ? "Sealing..." : "Seal & Save"}
          </button>
          <button className="tiv-btn" onClick={onCancel} data-testid="entity-cancel-btn">Discard</button>
        </>
      ) : (
        <>
          <button className="tiv-btn" onClick={onEdit} data-testid="entity-edit-btn">Edit Record</button>
          <button className="tiv-btn tiv-btn-danger" onClick={onRemove} data-testid="entity-delete-btn">Erase</button>
        </>
      )}
    </div>
  );
}
